import styled from "styled-components";
import { Box, Flex, Text } from "rebass";
import { HiCheck, HiExternalLink } from "react-icons/hi";
import { SecondaryButton } from "../styledComponents";
import { IClipForDeletion } from "../@types";
import { formatNumber } from "MVVM/Utilities/Clip";

type SortOption = "LATEST" | "VIEWS" | "BEST";

interface DeleteBrowseProps {
  clips: IClipForDeletion[];
  isLoading: boolean;
  selectedClipIds: string[];
  onSelectClip: (clipId: string) => void;
  onSortChange: (sort: SortOption) => void;
  currentSort: SortOption;
  onDelete: () => void;
  onBack: () => void;
  isDeleting: boolean;
  backButtonText?: string;
  hideSortOptions?: boolean;
  onOpenClip?: (clipId: string) => void;
  actionLabel?: string;
}

export const DeleteBrowse = ({
  clips,
  isLoading,
  selectedClipIds,
  onSelectClip,
  onSortChange,
  currentSort,
  onDelete,
  onBack,
  isDeleting,
  backButtonText = "Back",
  hideSortOptions = false,
  onOpenClip,
  actionLabel = "Remove",
}: DeleteBrowseProps) => {
  const handleOpenClip = (e: React.MouseEvent, clipId: string) => {
    e.stopPropagation();
    onOpenClip?.(clipId);
  };
  return (
    <>
      <Box p="24px 28px">
        {!hideSortOptions && (
          <SortContainer mb="16px">
            <SortLabel>Sort:</SortLabel>
            <SortButton
              $active={currentSort === "LATEST"}
              onClick={() => onSortChange("LATEST")}
            >
              Latest
            </SortButton>
            <SortButton
              $active={currentSort === "VIEWS"}
              onClick={() => onSortChange("VIEWS")}
            >
              Views
            </SortButton>
            <SortButton
              $active={currentSort === "BEST"}
              onClick={() => onSortChange("BEST")}
            >
              Score
            </SortButton>
          </SortContainer>
        )}

        <ClipList>
          {isLoading ? (
            <>
              {[...Array(4)].map((_, i) => (
                <SkeletonRow key={i} style={{ marginBottom: i < 3 ? 2 : 0 }}>
                  <SkeletonCheckbox />
                  <SkeletonThumbnail />
                  <SkeletonInfo>
                    <SkeletonTitle />
                    <SkeletonMeta />
                  </SkeletonInfo>
                </SkeletonRow>
              ))}
            </>
          ) : clips.length === 0 ? (
            <EmptyText>No clips found</EmptyText>
          ) : (
            clips.map((clip) => (
              <ClipRow
                key={clip.id}
                onClick={() => onSelectClip(clip.id)}
                $selected={selectedClipIds.includes(clip.id)}
              >
                <Checkbox $checked={selectedClipIds.includes(clip.id)}>
                  {selectedClipIds.includes(clip.id) && <HiCheck size={12} />}
                </Checkbox>
                <ClipThumbnail src={clip.thumbnailUrl} alt={clip.title} />
                <ClipInfo>
                  <ClipTitle>{clip.title}</ClipTitle>
                  <ClipMeta>{formatNumber(clip.views)} views</ClipMeta>
                </ClipInfo>
                {onOpenClip && (
                  <OpenButton onClick={(e) => handleOpenClip(e, clip.id)}>
                    <HiExternalLink size={16} />
                  </OpenButton>
                )}
              </ClipRow>
            ))
          )}
        </ClipList>
      </Box>

      <ModalFooter>
        <SecondaryButton onClick={onBack} disabled={isDeleting}>
          {backButtonText}
        </SecondaryButton>
        <ActionButton
          onClick={onDelete}
          disabled={selectedClipIds.length === 0 || isDeleting}
        >
          {isDeleting
            ? "Removing..."
            : `${actionLabel} Selected (${selectedClipIds.length})`}
        </ActionButton>
      </ModalFooter>
    </>
  );
};

const ModalFooter = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;
`;

const SortContainer = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const SortLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 13px;
  margin-right: 4px;
`;

const SortButton = styled.button<{ $active: boolean }>`
  background: ${({ theme, $active }) =>
    $active ? theme.colors.navy : "transparent"};
  border: none;
  border-radius: 6px;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.ice : theme.colors.shade400};
  cursor: pointer;
  font-family: Barlow;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  padding: 6px 12px;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const ClipList = styled(Box)`
  max-height: 280px;
  overflow-y: auto;
`;

const ClipRow = styled(Flex)<{ $selected: boolean }>`
  align-items: center;
  background: ${({ $selected }) =>
    $selected ? "rgba(255, 255, 255, 0.03)" : "transparent"};
  border-radius: 8px;
  cursor: pointer;
  gap: 12px;
  padding: 8px 10px;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }
`;

const Checkbox = styled(Box)<{ $checked: boolean }>`
  align-items: center;
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.alert : theme.colors.navy50};
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  width: 18px;
`;

const ClipThumbnail = styled.img`
  border-radius: 6px;
  flex-shrink: 0;
  height: 40px;
  object-fit: cover;
  width: 72px;
`;

const ClipInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClipMeta = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 12px;
`;

const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: 200px 0;
    }
  }
`;

const SkeletonBase = styled(Box)`
  ${shimmer}
  animation: shimmer 1.5s ease-in-out infinite;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.navy50} 0%,
    ${({ theme }) => theme.colors.navy} 50%,
    ${({ theme }) => theme.colors.navy50} 100%
  );
  background-size: 200px 100%;
  border-radius: 6px;
`;

const SkeletonRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
`;

const SkeletonCheckbox = styled(SkeletonBase)`
  border-radius: 4px;
  flex-shrink: 0;
  height: 18px;
  width: 18px;
`;

const SkeletonThumbnail = styled(SkeletonBase)`
  flex-shrink: 0;
  height: 40px;
  width: 72px;
`;

const SkeletonInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const SkeletonTitle = styled(SkeletonBase)`
  height: 14px;
  margin-bottom: 6px;
  width: 70%;
`;

const SkeletonMeta = styled(SkeletonBase)`
  height: 12px;
  width: 40%;
`;

const LoadingText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const EmptyText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade400};
  font-family: Barlow;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const OpenButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.shade400};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  transition: all 0.15s ease;
  width: 32px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.alert};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
